import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KyronClient } from 'components/utils/KyronClient';
import { API_V1 } from './constants';
import { useKyronQuery, keepPreviousData } from './kyronQuery';
import { useKyronMutationV2, PayloadAndRequestParams } from './kyronMutation';
import {
  Lesson,
  Pagination,
  LessonSegment,
  LessonSection,
  PublishVisibility,
  LessonValidation,
  MutationRespWithBT,
} from '../types';
import { devConsole } from '../../components/utils/logUtils';

export const useLessonsQuery = ({
  page = 1,
  perPage = 25,
  search,
}: { page?: number; perPage?: number; search?: string } = {}) => {
  const searchQueryStr = search ? `&q=${search}` : '';
  return useKyronQuery<{ lessons: Lesson[]; meta: Pagination }>(
    `/lessons?page=${page}&per_page=${perPage}${searchQueryStr}`,
    {
      queryKey: [`/lessons`, { page, perPage, ...(search ? { search } : {}) }],
      placeholderData: keepPreviousData,
      enabled: true,
    },
  );
};

export const useGetReadyForFinalizeQuery = (id: number) =>
  useKyronQuery<{
    data: boolean;
  }>(`/lessons/${id}/all_videos_attached`);

export const useLessonSegmentsQuery = ({
  lessonId,
  search = '',
  page = 1,
  perPage = 25,
}: {
  lessonId?: number;
  search?: string;
  page?: number;
  perPage?: number;
}) =>
  useKyronQuery<{ lesson_segments: LessonSegment[]; meta: Pagination }>(
    `/lessons/${lessonId}/lesson_segments?page=${page}&per_page=${perPage}&q=${search}`,
    {
      queryKey: [`/lessons/${lessonId}/lesson_segments`, { page, perPage, search }],
      placeholderData: keepPreviousData,
      enabled: !!lessonId,
    },
  );

export const useCreateLessonSegment = () =>
  useKyronMutationV2<PayloadAndRequestParams & { lessonId: number }, LessonSegment>(`/lesson_segments`, {
    method: 'POST',
    invalidatesMultiple: ({ lessonId }: { lessonId: number }) => [
      [`/lessons/${lessonId}/lesson_segments`],
      [`/lessons/${lessonId}/lesson_sections`],
    ],
  });

// TODO(Ege): Move this to lessonSectionHooks to reflect server's route structure better
export const useLessonSectionsQuery = (lessonId?: number | string, disabled?: boolean) =>
  useKyronQuery<{ lesson_sections: LessonSection[] }>(`/lessons/${lessonId}/lesson_sections`, {
    placeholderData: keepPreviousData,
    enabled: !!lessonId && !disabled,
  });

export const useLessonQuery = (lessonId?: number | string) =>
  useKyronQuery<Lesson>(`/lessons/${lessonId}`, { enabled: !!lessonId });

/**
 * Fetches the lesson based on the lessonId URL param
 */
export const useLessonFromParams = () => {
  const { lessonId } = useParams();
  return useLessonQuery(Number(lessonId));
};

export const useUpdateLesson = () =>
  useKyronMutationV2<PayloadAndRequestParams & { lessonId: number }, MutationRespWithBT<Lesson>>(`/lessons/:lessonId`, {
    method: 'PUT',
    invalidates: ({ lessonId }: { lessonId: number }) => [`/lessons/${lessonId}`],
  });

export const useDeleteLesson = () =>
  useKyronMutationV2(`/lessons/:lessonId`, {
    method: 'DELETE',
    invalidates: ({ lessonId }: { lessonId: number }) => [`/lessons/${lessonId}`],
  });

export const usePublishLesson = () =>
  useKyronMutationV2<{ lessonId: number; payload?: { visibility: PublishVisibility } }, MutationRespWithBT<Lesson>>(
    '/lessons/:lessonId/publish',
    {
      method: 'POST',
      invalidatesMultiple: ({ lessonId }: { lessonId: number }) => [
        [`/lessons/${lessonId}`],
        [`/lessons/${lessonId}/lesson_sections`],
        [`/lessons/${lessonId}/get_latest_draft_lesson`], // an invalidation will trigger a redirect to the latest draft by useRedirectToLatestDraft
        ['/lesson_collections/library'],
        ['/lesson_collections/marketplace'],
      ],
    },
  );

// Redirects to the latest draft when it is available
export const useRedirectToLatestDraft = () => {
  const navigate = useNavigate();
  const { lessonId: currentLessonId } = useParams<{ lessonId: string }>();
  const { data: latestDraft } = useKyronQuery<Lesson>(`/lessons/${currentLessonId}/get_latest_draft_lesson`, {
    enabled: !!currentLessonId,
  });

  if (latestDraft && latestDraft.id.toString() !== currentLessonId) {
    devConsole.debug(`Found a new draft, redirecting to new draft lesson id: ${latestDraft.id}`);

    // Redirect to latest draft
    navigate(`/studio/courses/${latestDraft.id}`, { replace: true });
  }
};

export const useLessonValidateQuery = (lessonId?: number) =>
  useKyronQuery<LessonValidation[]>(`/lessons/${lessonId}/validate`, {
    enabled: !!lessonId,
  });

export const useDownloadScorm = (lessonId: number) => {
  const client = new KyronClient(); // create client in hook for testing
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = async () => {
    setIsPending(true);
    try {
      const data = await client.getDataWithError<Blob>(`${API_V1}/lessons/${lessonId}/scorm_2004_download`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/zip',
        },
      });

      // Download the SCORM file to browser
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${lessonId}-scorm.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to fetch SCORM file:', error);
      throw error; // let the error bubble up to component
    } finally {
      setIsPending(false);
    }
  };
  return { isPending, mutateAsync };
};

export const useInitLesson = () =>
  useKyronMutationV2<PayloadAndRequestParams, Lesson>('/lessons/init', { method: 'POST' });
